/**
 * The global chrome: top bar, navigation drawer, and the routed outlet.
 *
 * Classroom-faithful: a white app bar with a hamburger on the left and the
 * account avatar on the right, a permanent drawer on desktop that collapses to
 * a temporary overlay on mobile, and the enrolled-course list living inside the
 * drawer rather than in a separate nav.
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChecklistIcon from '@mui/icons-material/ChecklistOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeedOutlined';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';

import { useCourses, useSelf } from '@/lib/canvas/queries';
import { initials } from '@/lib/format';
import { useThemeMode } from '@/theme/themeMode';
import { tokens } from '@/theme/tokens';

import { ErrorBoundary } from './ErrorBoundary';

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const PRIMARY_LINKS: NavLink[] = [
  { to: '/', label: 'Home', icon: <HomeIcon /> },
  { to: '/stream', label: 'Stream', icon: <DynamicFeedIcon /> },
  { to: '/calendar', label: 'Calendar', icon: <CalendarTodayIcon /> },
  { to: '/todo', label: 'To-do', icon: <ChecklistIcon /> },
];

const SECONDARY_LINKS: NavLink[] = [
  { to: '/archived', label: 'Archived classes', icon: <ArchiveOutlinedIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

export function AppShell() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Closing on navigation is the behaviour people expect from a temporary
  // drawer; leaving it open over the new page reads as a bug.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <TopBar onMenuClick={() => setDrawerOpen((open) => !open)} />

      <Box
        component="nav"
        sx={{ width: { md: tokens.size.drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="Main navigation"
      >
        {/* Temporary on mobile: overlays content, closes on navigate. */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: tokens.size.drawerWidth,
            },
          }}
        >
          <Toolbar />
          <DrawerContents />
        </Drawer>

        {/* Permanent on desktop, exactly like Classroom. */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: tokens.size.drawerWidth,
            },
          }}
        >
          <Toolbar />
          <DrawerContents />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${tokens.size.drawerWidth}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: tokens.size.appBarHeightMobile, sm: tokens.size.appBarHeight } }} />
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Top bar                                                             */
/* ------------------------------------------------------------------ */

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { data: self } = useSelf();
  const { mode, toggle } = useThemeMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      elevation={0}
    >
      <Toolbar sx={{ minHeight: { xs: tokens.size.appBarHeightMobile, sm: tokens.size.appBarHeight } }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          sx={{ mr: 1, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'inherit',
            mr: 'auto',
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: tokens.fontWeight.regular }}>
            C-GC
          </Typography>
        </Box>

        <Tooltip title={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
          <IconButton onClick={toggle} aria-label="Toggle theme">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={self?.name ?? 'Account'}>
          <IconButton
            onClick={(event) => setAnchor(event.currentTarget)}
            aria-label="Account menu"
            sx={{ ml: 0.5 }}
          >
            <Avatar
              src={self?.avatar_url}
              alt=""
              sx={{
                width: tokens.size.avatarMd,
                height: tokens.size.avatarMd,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: tokens.fontSize.sm,
              }}
            >
              {initials(self?.name)}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, py: 1.5, minWidth: tokens.size.drawerWidth * 0.7 }}>
            <Typography variant="subtitle2">{self?.name ?? 'Not signed in'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {self?.email ?? 'Add a token in Settings'}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchor(null);
              navigate('/settings');
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchor(null);
              navigate('/settings#token');
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Manage token
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

/* ------------------------------------------------------------------ */
/* Drawer contents                                                     */
/* ------------------------------------------------------------------ */

function DrawerContents() {
  const location = useLocation();
  const { data: courses, isPending } = useCourses('active');

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <Box sx={{ overflowY: 'auto', pb: 2 }}>
      <List sx={{ px: 1 }}>
        {PRIMARY_LINKS.map((link) => (
          <ListItem key={link.to} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton component={RouterLink} to={link.to} selected={isActive(link.to)}>
              <ListItemIcon>{link.icon}</ListItemIcon>
              <ListItemText primary={link.label} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2 }} />

      <List
        sx={{ px: 1 }}
        subheader={
          <ListSubheader
            disableSticky
            sx={{ bgcolor: 'transparent', fontSize: tokens.fontSize.sm, lineHeight: 2.5 }}
          >
            Enrolled
          </ListSubheader>
        }
      >
        {isPending
          ? Array.from({ length: 4 }, (_, i) => (
              <ListItem key={i} sx={{ px: 2 }}>
                <Skeleton width="80%" height={24} />
              </ListItem>
            ))
          : (courses ?? []).map((course) => (
              <ListItem key={course.id} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  component={RouterLink}
                  to={`/c/${course.id}`}
                  selected={location.pathname.startsWith(`/c/${course.id}`)}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        width: tokens.size.avatarSm,
                        height: tokens.size.avatarSm,
                        fontSize: tokens.fontSize.xs,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                      }}
                    >
                      {initials(course.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={course.name}
                    secondary={course.course_code}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        {!isPending && (courses ?? []).length === 0 ? (
          <ListItem sx={{ px: 2 }}>
            <Typography variant="caption" color="text.secondary">
              No active courses
            </Typography>
          </ListItem>
        ) : null}
      </List>

      <Divider sx={{ mx: 2 }} />

      <List sx={{ px: 1 }}>
        {SECONDARY_LINKS.map((link) => (
          <ListItem key={link.to} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton component={RouterLink} to={link.to} selected={isActive(link.to)}>
              <ListItemIcon>{link.icon}</ListItemIcon>
              <ListItemText primary={link.label} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
